import { useState } from 'react';
import { Pause, Play, Check, X, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { cn, formatDateTime } from '@/lib/utils';
import {
  PAUSE_REASONS, pauseReasonLabel, pauseScopeLabel, pauseBusinessMs, isOpenPause, canPauseOrder,
  type PauseReason, type PauseRow,
} from '@/lib/pauses';
import { formatDurationMs } from '@/lib/stageTimeline';
import { usePauseMutations } from '@/lib/api/pauses';

interface OrderRef { id: string; order_number?: string | null; quantity?: number | null; status?: string | null }

const who = (t: PauseRow['paused_by_tech']) => t?.alias || t?.name || null;

// ── Pause dialog ────────────────────────────────────────────────────────────

/**
 * Button that opens the pause dialog. On a multi-unit order the technician
 * chooses the whole order or one unit; `defaultUnit` preselects the unit they
 * are looking at.
 */
export function PauseButton({
  order, pauses, defaultUnit, size = 'sm', className,
}: {
  order: OrderRef;
  pauses: ReadonlyArray<PauseRow>;
  defaultUnit?: number | null;
  size?: 'sm' | 'default';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const unitCount = Math.max(order.quantity || 1, 1);
  const openPauses = pauses.filter(isOpenPause);
  const wholeOrderPaused = openPauses.some(p => p.unit_number == null);
  const pausedUnits = new Set(openPauses.map(p => p.unit_number).filter((u): u is number => u != null));
  const allUnitsPaused = wholeOrderPaused || pausedUnits.size >= unitCount;

  if (!canPauseOrder(order.status) || allUnitsPaused) return null;

  return (
    <>
      <Button
        variant="outline"
        size={size}
        className={cn('h-8 border-amber-300 text-amber-800 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:hover:bg-amber-950', className)}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <Pause className="h-3.5 w-3.5 mr-1" /> Pause
      </Button>
      {open && (
        <PauseDialog
          order={order}
          unitCount={unitCount}
          pausedUnits={pausedUnits}
          defaultUnit={defaultUnit && !pausedUnits.has(defaultUnit) && unitCount > 1 ? defaultUnit : null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function PauseDialog({
  order, unitCount, pausedUnits, defaultUnit, onClose,
}: {
  order: OrderRef;
  unitCount: number;
  pausedUnits: Set<number>;
  defaultUnit: number | null;
  onClose: () => void;
}) {
  const { pause } = usePauseMutations();
  // null = whole order
  const [unit, setUnit] = useState<number | null>(defaultUnit);
  const [reason, setReason] = useState<PauseReason | ''>('');
  const [note, setNote] = useState('');

  const noteRequired = reason === 'other';
  const canSubmit = !!reason && (!noteRequired || note.trim().length > 0) && !pause.isPending;

  const submit = () => {
    if (!reason) return;
    pause.mutate({ order, unit, reason, note }, { onSuccess: onClose });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Pause build</DialogTitle>
          <DialogDescription>
            {order.order_number}. The clock stops until you resume. A manager approves the pause when the order is reviewed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {unitCount > 1 && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">What is stopped?</p>
              <div className="flex flex-wrap gap-1.5">
                <ScopeChip active={unit == null} disabled={pausedUnits.size > 0} onClick={() => setUnit(null)}>
                  Whole order
                </ScopeChip>
                {Array.from({ length: unitCount }, (_, i) => i + 1).map(u => (
                  <ScopeChip key={u} active={unit === u} disabled={pausedUnits.has(u)} onClick={() => setUnit(u)}>
                    Unit {u}{pausedUnits.has(u) && ' (paused)'}
                  </ScopeChip>
                ))}
              </div>
              {pausedUnits.size > 0 && (
                <p className="text-xs text-muted-foreground">Resume the paused units first to pause the whole order.</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="pause-reason" className="text-sm font-medium">Reason</label>
            <select
              id="pause-reason"
              className="w-full h-9 rounded-md border bg-background px-2 text-sm"
              value={reason}
              onChange={e => setReason(e.target.value as PauseReason)}
            >
              <option value="" disabled>Choose a reason…</option>
              {(Object.keys(PAUSE_REASONS) as PauseReason[]).map(r => (
                <option key={r} value={r}>{PAUSE_REASONS[r]}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="pause-note" className="text-sm font-medium">
              Details {noteRequired ? '' : <span className="font-normal text-muted-foreground">(optional)</span>}
            </label>
            <textarea
              id="pause-note"
              rows={3}
              className="w-full p-2 text-sm border rounded-md resize-none bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={reason === 'missing_parts' ? 'Which part is missing?' : 'What happened?'}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={!canSubmit} onClick={submit}>
            <Pause className="h-4 w-4 mr-1" />
            {pause.isPending ? 'Pausing…' : unit == null ? (unitCount > 1 ? 'Pause whole order' : 'Pause') : `Pause unit ${unit}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeChip({ active, disabled, onClick, children }: { active: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
        active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted border-border',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-background',
      )}
    >
      {children}
    </button>
  );
}

// ── Open pauses, with Resume ────────────────────────────────────────────────

/** One amber row per running pause, each with its own Resume button. */
export function OpenPauses({
  order, pauses, canResume = true, className,
}: {
  order: OrderRef;
  pauses: ReadonlyArray<PauseRow>;
  canResume?: boolean;
  className?: string;
}) {
  const { resume } = usePauseMutations();
  const open = pauses.filter(isOpenPause).sort((a, b) => (a.unit_number ?? 0) - (b.unit_number ?? 0));
  if (!open.length) return null;
  const multi = (order.quantity || 1) > 1;

  return (
    <div className={cn('space-y-1.5', className)}>
      {open.map(p => (
        <div
          key={p.id}
          className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="min-w-0">
            <p className="font-medium">
              <Pause className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
              {multi ? `${pauseScopeLabel(p)} paused` : 'Paused'} · {pauseReasonLabel(p.reason)}
            </p>
            <p className="text-xs opacity-80 truncate">
              Since {formatDateTime(p.paused_at)}
              {who(p.paused_by_tech) && ` by ${who(p.paused_by_tech)}`}
              {p.note && ` · ${p.note}`}
            </p>
          </div>
          {canResume && (
            <Button
              size="sm"
              className="h-7 shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
              disabled={resume.isPending}
              onClick={() => resume.mutate({ order, pause: p })}
            >
              <Play className="h-3.5 w-3.5 mr-1" /> Resume
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Pipeline badge ──────────────────────────────────────────────────────────

/** Compact marker for a pipeline card or list row: "Paused · Missing parts". */
export function PausedBadge({ pauses, quantity, className }: { pauses: ReadonlyArray<PauseRow>; quantity?: number | null; className?: string }) {
  const open = pauses.filter(isOpenPause);
  if (!open.length) return null;
  const whole = open.find(p => p.unit_number == null);
  const units = open.filter(p => p.unit_number != null).map(p => p.unit_number).sort((a, b) => a! - b!);
  const scope = whole || (quantity ?? 1) <= 1 ? '' : ` · U${units.join(', U')}`;
  const first = whole ?? open[0];
  const title = open
    .map(p => `${pauseScopeLabel(p)}: ${pauseReasonLabel(p.reason)} since ${formatDateTime(p.paused_at)}${p.note ? ` (${p.note})` : ''}`)
    .join('\n');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700',
        className,
      )}
      title={title}
    >
      <Pause className="h-2.5 w-2.5" />
      Paused · {pauseReasonLabel(first.reason)}{scope}
    </span>
  );
}

// ── Review list ─────────────────────────────────────────────────────────────

/**
 * Every pause on the order, with approve / reject for reviewers. Approved
 * pauses come off the assembly duration; rejected ones count as work.
 */
export function PauseReviewList({
  order, pauses, canReview, activeDays, className,
}: {
  order: OrderRef;
  pauses: ReadonlyArray<PauseRow>;
  canReview: boolean;
  activeDays?: Set<string>;
  className?: string;
}) {
  const { review } = usePauseMutations();
  if (!pauses.length) return null;
  const pending = pauses.filter(p => p.review_status === 'pending').length;
  const multi = (order.quantity || 1) > 1 || pauses.some(p => p.unit_number != null);

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pauses</p>
        {pending > 0 && (
          <span className="text-[11px] font-medium text-amber-700 dark:text-amber-500">
            {pending} awaiting {canReview ? 'your ' : ''}decision
          </span>
        )}
      </div>
      <div className="space-y-2">
        {pauses.map(p => {
          const open = isOpenPause(p);
          const ms = pauseBusinessMs(p, activeDays);
          return (
            <div
              key={p.id}
              className={cn(
                'rounded-md border p-2 text-sm',
                p.review_status === 'approved' && 'border-green-300 bg-green-50/60 dark:border-green-800 dark:bg-green-950/40',
                p.review_status === 'rejected' && 'border-red-300 bg-red-50/60 dark:border-red-800 dark:bg-red-950/40',
                p.review_status === 'pending' && 'border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/40',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {multi && <span className="text-muted-foreground font-normal">{pauseScopeLabel(p)} · </span>}
                    {pauseReasonLabel(p.reason)}
                    <span className="ml-2 font-mono text-xs">{formatDurationMs(ms)}</span>
                    {open && <span className="ml-1 text-xs text-amber-700 dark:text-amber-500">(still running)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(p.paused_at)} → {open ? 'now' : formatDateTime(p.resumed_at)}
                    {who(p.paused_by_tech) && ` · ${who(p.paused_by_tech)}`}
                  </p>
                  {p.note && <p className="text-xs mt-0.5 whitespace-pre-wrap">{p.note}</p>}
                  {p.review_status !== 'pending' && (
                    <p className="text-[11px] mt-0.5 text-muted-foreground">
                      {p.review_status === 'approved' ? 'Approved' : 'Rejected: counted as work'}
                      {who(p.reviewed_by_tech) && ` by ${who(p.reviewed_by_tech)}`}
                      {p.reviewed_at && `, ${formatDateTime(p.reviewed_at)}`}
                    </p>
                  )}
                </div>
                {canReview && !open && (
                  <div className="flex shrink-0 gap-1">
                    {p.review_status === 'pending' ? (
                      <>
                        <Button
                          size="sm" variant="outline"
                          className="h-7 px-2 border-green-400 text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950"
                          disabled={review.isPending}
                          onClick={() => review.mutate({ order, pause: p, status: 'approved' })}
                          title="Take this time off the assembly duration"
                        >
                          <Check className="h-3.5 w-3.5 mr-0.5" /> Approve
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="h-7 px-2 border-red-300 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                          disabled={review.isPending}
                          onClick={() => review.mutate({ order, pause: p, status: 'rejected' })}
                          title="Count this time as assembly time"
                        >
                          <X className="h-3.5 w-3.5 mr-0.5" /> Reject
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm" variant="ghost" className="h-7 px-2 text-xs"
                        disabled={review.isPending}
                        onClick={() => review.mutate({ order, pause: p, status: 'pending' })}
                        title="Undo this decision"
                      >
                        <Undo2 className="h-3.5 w-3.5 mr-0.5" /> Undo
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
